import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import {
	HlrCurrenciesCreateResponse,
	HlrCurrenciesDeleteRequest,
	HlrCurrenciesDeleteResponse,
	HlrCurrenciesReadRequest,
	HlrCurrenciesReadResponse,
	HlrCurrenciesUpdateResponse,
	HlrSystemCurrenciesResponse
} from '@helpers/types/hlr';
import { 
	AbstractCRUDService, 
	AlarisApiService, 
	AlarisLocalStorageService, 
	ExtendableRefBookService 
} from '@campaign-portal/components-library';
import { HlrCurrencies } from '@helpers/types/models';
import { ReadRequest, ReadResponse } from '@campaign-portal/namespace/common/implementations';
import { Filter, RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { STORAGE } from '@helpers/types/consts';

@Injectable({
	providedIn: 'root'
})
export class CurrenciesService extends ExtendableRefBookService<HlrCurrencies> implements AbstractCRUDService {


	readonly entity = '';
	readonly title = '';
	override readonly loading$ = new BehaviorSubject<boolean>(false);
	readonly create = this.update;

	constructor(
		private readonly api: AlarisApiService,
		private readonly storage: AlarisLocalStorageService
	) {
		super();
	}

	override process(resp: ReadResponse<HlrCurrencies<number | null>[]>): ReadResponse<HlrCurrencies<number | null>[]> {
		this.list$.next(resp.Data);
		this.map$.next(this.list.reduce((result, item) => {
			result.set(item.id, item);
			return result;
		}, new Map()));
		return resp;
	}

	read(params?: ReadRequest): Observable<ReadResponse<HlrCurrencies[]>> {
		let parsedParams: HlrCurrenciesReadRequest = {
			active: 'name',
			direction: 'asc',
		}
		if (params) {
			parsedParams = this.prepareParams(params);
		}
		return this.api.loader<HlrCurrenciesReadResponse>('Currencies.Read', parsedParams, this.loading$)
			.pipe(
				map((resp) => {
					return {
						Success: !!resp.data,
						Total: resp.length,
						Data: resp.data
					};
				}),
				map((resp) => {
					return this.process(resp);
				})
			);
	}

	update(params: HlrCurrencies): Observable<HlrCurrenciesUpdateResponse | HlrCurrenciesCreateResponse> {
		const method = params.id ? 'Currencies.Update' : 'Currencies.Create';
		return this.api.loader<HlrCurrenciesUpdateResponse 
			| HlrCurrenciesCreateResponse>(method, params, this.loading$);
	}

	delete(params: HlrCurrenciesDeleteRequest): Observable<HlrCurrenciesDeleteResponse> {
		return this.api.loader<HlrCurrenciesDeleteResponse>('Currencies.Delete', params, this.loading$);
	}

	getSystemCurrency(): Observable<HlrCurrencies> {
		return this.api.loader<HlrSystemCurrenciesResponse>('Currencies.GetSysCurrency').pipe(
			map(res => res.sysCurrency)
		)
	}

	readActive(): Observable<ReadResponse<HlrCurrencies[]>> {
		return this.api.loader<HlrCurrenciesReadResponse>('Currencies.GetActive', {}, this.loading$)
			.pipe(
				map((resp) => {
					return {
						Success: !!resp.data,
						Total: resp.length,
						Data: resp.data
					};
				}),
				map((resp) => {
					return this.process(resp);
				})
			);
	}

	private prepareParams(params: RPCRequestParams): HlrCurrenciesReadRequest {
		return {
			offset: params.Paging?.Skip ?? 0,
			limit: params.Paging?.Take ?? parseInt(this.storage.get(STORAGE.PAGE_SIZE) ?? '25', 10),
			active: 'name',
			direction: 'asc',
			filters: params.Filters
				? params.Filters.map((filter) => {
					return this.parseFilterField(filter);
				})
				: []
		};
	}

	private parseFilterField(filter: Filter): { key: string, value: string } {
		const variable = filter.Field;
		const value = filter.Value;
		switch (variable) {
			case 'name':
				return { key: 'name', value: value + '' };
			case 'currencySymbol':
				return { key: 'currency_symbol', value: value + '' };
			default:
				return { key: variable, value: value + '' };
		}
	}

}
